import Box from "@mui/material/Box";
import type { JSX } from "react";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { type FieldErrors, type TopicsState, type RelevantTopicAnchorRow, type UrlGroup, type TaskType } from "./useTaskCreation";
import { AnchorTopicEstimate } from "./AnchorTopicEstimate";
import { FixedRelevantTopicSection } from "./FixedRelevantTopicSection";
import { RelevantTopicAnchorsSection } from "./RelevantTopicAnchorsSection";
import { isPublicationTaskType } from "./IsPublicationTaskType";

export function UrlGroupCard(props: {
  index: number;
  group: UrlGroup;
  taskType: TaskType;
  isOnlyGroup: boolean;
  topicsState: TopicsState;
  groupErrors: FieldErrors;
  disabled: boolean;
  onRemove: () => void;
  onChangeField: (key: "targetUrl" | "anchorsText" | "comment", value: string) => void;
  onProcess: () => void;
  onResetToTextarea: () => void;
  onRetryTopics: () => void;
  onSetAnchorRow: (
    anchorIndex: number,
    field: keyof RelevantTopicAnchorRow,
    value: string | number,
  ) => void;
  onSetUniqueArticlesCount: (value: number) => void;
  onSetTopicRow: (topicIndex: number, value: string) => void;
}) {
  const { group, taskType, groupErrors, isOnlyGroup } = props;
  const groupNumber = props.index + 1;
  const isRelevantTopicTable =
    taskType === "generation_relevant_topic" && group.anchorInputMode.mode === "table";

  let anchorsSection: JSX.Element;
  if (taskType === "generation_relevant_topic") {
    anchorsSection = (
        <RelevantTopicAnchorsSection
          anchorsText={group.anchorsText}
          anchorInputMode={group.anchorInputMode}
          topicsState={props.topicsState}
          groupErrors={groupErrors}
          disabled={props.disabled}
          onChangeAnchorsText={(value) => props.onChangeField("anchorsText", value)}
          onProcess={props.onProcess}
          onResetToTextarea={props.onResetToTextarea}
          onRetryTopics={props.onRetryTopics}
          onSetAnchorRow={props.onSetAnchorRow}
        />
    );
  } else if (taskType === "generation_relevant_topic_fixed") {
    anchorsSection = (
        <FixedRelevantTopicSection
          group={group}
          topicsState={props.topicsState}
          groupErrors={groupErrors}
          disabled={props.disabled}
          onChangeAnchorsText={(value) => props.onChangeField("anchorsText", value)}
          onSetUniqueArticlesCount={props.onSetUniqueArticlesCount}
          onProcess={props.onProcess}
          onResetToTextarea={props.onResetToTextarea}
          onRetryTopics={props.onRetryTopics}
          onSetTopicRow={props.onSetTopicRow}
        />
    );
  } else {
    anchorsSection = (
        <Box sx={{ mb: 2 }}>
          <TextField
            label="Анкоры"
            placeholder={
              isPublicationTaskType(taskType)
                ? "anchor,count\nbest product,3\ncheap service;5"
                : "anchor,count или anchor;count\nbest product,3\ncheap service;5"
            }
            value={group.anchorsText}
            error={!!groupErrors.anchors}
            helperText={
              groupErrors.anchors ??
              (isPublicationTaskType(taskType)
                ? "По одному на строку в формате: anchor,количество сайтов-доноров"
                : "По одному на строку в формате: anchor,количество или anchor;количество")
            }
            disabled={props.disabled}
            onChange={(event) => props.onChangeField("anchorsText", event.target.value)}
            multiline
            minRows={4}
            fullWidth
            size="small"
          />
          {taskType === "generation_anchor_topic" && (
            <AnchorTopicEstimate anchorsText={group.anchorsText} />
          )}
        </Box>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, maxWidth: isRelevantTopicTable ? undefined : 640 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle1" fontWeight="bold">
          URL-группа {groupNumber}
        </Typography>
        {!isOnlyGroup && (
          <IconButton
            size="small"
            aria-label={`Удалить URL-группу ${groupNumber}`}
            disabled={props.disabled}
            onClick={props.onRemove}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      <TextField
        label="Целевой URL"
        value={group.targetUrl}
        error={!!groupErrors.targetUrl}
        helperText={groupErrors.targetUrl}
        disabled={props.disabled}
        onChange={(event) => props.onChangeField("targetUrl", event.target.value)}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
      />

      {anchorsSection}

      <TextField
        label="Комментарий (необязательно)"
        value={group.comment}
        disabled={props.disabled}
        onChange={(event) => props.onChangeField("comment", event.target.value)}
        multiline
        minRows={2}
        fullWidth
        size="small"
      />
    </Paper>
  );
}
